import { CloseHealth } from "@common/types/closeHealth";
import type { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import type { BoardLaneDef } from "@design-system/Board/Board.types";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import type {
    ClientsWorkOverviewGroupRow,
    ClientsWorkOverviewWorkRow,
} from "./clientsWorkOverviewViewConfig";
import { compareWorkRows } from "./workRowCompare";
import type { WorkRowGroup } from "./workRowGroups";

export const WORK_PERSON_ROLES = ["preparer", "reviewer", "manager"] as const;

/** An engagement team role: a List grouping and a Board swimlane. */
export type WorkPersonRole = (typeof WORK_PERSON_ROLES)[number];

/** Each role's people on a row and its "No {role}" group's title. */
export const PERSON_ROLE_FIELDS: Record<
    WorkPersonRole,
    {
        field: "preparers" | "reviewers" | "managers";
        noneTitle: string;
    }
> = {
    preparer: {
        field: "preparers",
        noneTitle: "No preparer",
    },
    reviewer: {
        field: "reviewers",
        noneTitle: "No reviewer",
    },
    manager: { field: "managers", noneTitle: "No manager" },
};

export const isWorkPersonRole = (value: string): value is WorkPersonRole =>
    WORK_PERSON_ROLES.some((role) => role === value);

/** A person's group of engagements; `userId` is null for "No {role}". */
export interface WorkPersonGroup extends WorkRowGroup {
    userId: number | null;
}

const getPersonGroupId = (role: WorkPersonRole, userId: number | null) =>
    `${role}:${userId ?? "none"}`;

const getPersonCopyRowId = ({
    groupId,
    rowId,
}: {
    groupId: string;
    rowId: string;
}) => `${groupId}/${rowId}`;

/** The engagement row a person-group copy was made from; a plain row id maps to itself. */
export const getPersonCopySourceRowId = (rowId: string) =>
    rowId.slice(rowId.lastIndexOf("/") + 1);

/**
 * The person groups an engagement is in: one per person in the role, in the
 * row's order, or "No {role}". Ids carry the role, so a preparer's group never
 * shares one with the same person's reviewer group.
 */
export const getWorkPersonGroupIds = ({
    role,
    row,
}: {
    role: WorkPersonRole;
    row: ClientsWorkOverviewWorkRow;
}): string[] => {
    const members = row[PERSON_ROLE_FIELDS[role].field];
    return members.length === 0
        ? [getPersonGroupId(role, null)]
        : [
              ...new Set(
                  members.map(({ userId }) => getPersonGroupId(role, userId)),
              ),
          ];
};

/**
 * One group per person in the role on the page, by name A-Z, then "No {role}"
 * last (returned even when empty). An engagement is in the group of each of
 * its people, so it shows once per person. Rows keep their input order.
 */
export const getWorkPersonGroups = ({
    role,
    rows,
}: {
    role: WorkPersonRole;
    rows: readonly ClientsWorkOverviewWorkRow[];
}): WorkPersonGroup[] => {
    const { field, noneTitle } = PERSON_ROLE_FIELDS[role];
    const noneGroup: WorkPersonGroup = {
        id: getPersonGroupId(role, null),
        title: noneTitle,
        userId: null,
        rows: [],
    };
    const groupsByUserId = new Map<number, WorkPersonGroup>();
    for (const row of rows) {
        if (row[field].length === 0) {
            noneGroup.rows.push(row);
        }
        for (const { userId, preferredName, email } of row[field]) {
            const group = groupsByUserId.get(userId) ?? {
                id: getPersonGroupId(role, userId),
                title: preferredName ?? email,
                userId,
                rows: [],
            };
            if (group.rows.at(-1) !== row) {
                group.rows.push(row);
            }
            groupsByUserId.set(userId, group);
        }
    }
    const personGroups = [...groupsByUserId.values()].toSorted(
        (first, second) =>
            first.title.localeCompare(second.title) ||
            first.id.localeCompare(second.id),
    );
    return [...personGroups, noneGroup];
};

/**
 * A person group's header details after the count: "{time} left" from the
 * person's remaining estimates, left off when none remains or for "No
 * {role}", and "{n} at risk", left off at zero.
 */
export const getWorkPersonGroupDetails = ({
    userId,
    rows,
}: Pick<WorkPersonGroup, "userId" | "rows">): string[] => {
    const minutesLeft = rows
        .flatMap((row) => row.estimatedTimeByMember)
        .filter((estimate) => userId !== null && estimate.userId === userId)
        .reduce((total, estimate) => total + estimate.remainingMinutes, 0);
    const timeLeft = formatDuration(minutesLeft);
    const atRiskCount = rows.filter(
        (row) => row.health === CloseHealth.AT_RISK,
    ).length;
    return [
        ...(timeLeft ? [`${timeLeft} left`] : []),
        ...(atRiskCount > 0 ? [`${atRiskCount} at risk`] : []),
    ];
};

/**
 * List's groups for a person grouping, "No {role}" last and empty groups
 * left out. An engagement shows under each of its people, each copy with its
 * own row id, and each header reads "{count} · {time} left · {n} at risk"
 * after the name.
 */
export const getWorkPersonGroupRows = ({
    role,
    rows,
    sort,
}: {
    role: WorkPersonRole;
    rows: readonly ClientsWorkOverviewWorkRow[];
    sort: ClientsWorkOverviewSortValue | undefined;
}): ClientsWorkOverviewGroupRow[] =>
    getWorkPersonGroups({ role, rows })
        .filter((group) => group.rows.length > 0)
        .map((group) => ({
            id: group.id,
            rowType: "group",
            name: group.title,
            summary: [
                String(group.rows.length),
                ...getWorkPersonGroupDetails(group),
            ].join(" · "),
            children: group.rows
                .toSorted((first, second) =>
                    compareWorkRows(first, second, sort),
                )
                .map((row) => ({
                    ...row,
                    id: getPersonCopyRowId({
                        groupId: group.id,
                        rowId: row.id,
                    }),
                })),
        }));

/** A person lane titled "{name} · {time} left · {n} at risk"; the lane header already shows the count. */
export const getWorkPersonLaneWithDetails = (
    group: WorkPersonGroup,
): BoardLaneDef => ({
    id: group.id,
    title: [group.title, ...getWorkPersonGroupDetails(group)].join(" · "),
    label: group.title,
});
